import { Controller } from "@hotwired/stimulus";
import "../stylesheets/link_in_bio/editor.scss";
import "../stylesheets/link_in_bio/mobile_editor.scss";
import MobileEditor from "./link_in_bio_mobile_editor";

const PAGE_CLASS = "c-link_in_bio--page";
const TARGET_ROW_SELECTOR = "[data-link-in-bio-editor-target-picker-target~='row']";
const TARGET_INPUT_SELECTOR = "[data-link-in-bio-editor-target-picker-target~='selection']";
const THUMBNAIL_INPUT_SELECTOR = "[data-link-in-bio-editor-thumbnail-target~='input']";
const THUMBNAIL_REMOVAL_SELECTOR = "[data-link-in-bio-editor-thumbnail-target~='removal']";
const NON_TEXT_INPUT_TYPES = new Set([ "button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit" ]);
const KEYBOARD_OCCLUSION_THRESHOLD = 80;
const KEYBOARD_DIALOG_STYLE_PROPERTIES = [ "inset", "margin", "max-height", "translate" ];

export default class extends Controller {
  static targets = [
    "saveState", "saveLabel", "liveState", "publishButton", "discardButton", "previewFrame", "previewScreen", "previewContent",
    "sectionTemplate", "itemTemplate", "deleteDialog", "itemDeleteDialog", "discardDialog", "sectionLimitDialog",
    "publicLink", "copyButton", "slugDisplay", "slugValue", "slugChangeButton", "slugApplyButton", "slugLockIcon",
    "slugEditor", "slugInput", "slugError", "slugConfirmDialog",
  ];

  static values = {
    mobileLabels: Object,
    draftUrl: String,
    publishUrl: String,
    discardUrl: String,
    slugAvailabilityUrl: String,
    slugUpdateUrl: String,
    publicUrl: String,
    editorPayload: { type: Object, default: {} },
    publishedPayload: { type: Object, default: {} },
    draftAssetKeys: { type: Array, default: [] },
    initiallyLive: Boolean,
    savingMessage: String,
    savedMessage: String,
    saveFailedMessage: String,
    discardErrorMessage: String,
    invalidMessage: String,
    networkErrorMessage: String,
    invalidUrlMessage: String,
    itemTemplateKey: String,
    sectionTemplateKey: String,
    maxSections: Number,
    maxItems: Number,
    itemLimitMessage: String,
    conflictMessage: String,
    invalidFieldLabels: Object,
    slugErrors: Object,
    slugConfirmationMessage: String,
    slugCooldownLabels: Object,
  };

  connect() {
    this.lockVersion = Number(this.editorPayloadValue.lock_version || 0);
    this.deletedSectionIds = new Set(this.editorPayloadValue.deleted_section_ids || []);
    this.deletedItemIds = new Set(this.editorPayloadValue.deleted_item_ids || []);
    this.itemEditSnapshots = new WeakMap();
    this.pendingThumbnailFiles = new Map();
    this.thumbnailObjectUrls = new Map();
    this.hasPublished = this.initiallyLiveValue;
    this.isSaving = false;
    this.element.inert = false;
    this.allowNavigation = false;
    this.saveError = false;
    this.saveErrorTimer = null;
    this.savedFileSignatures = this.persistedAssetSignatures();
    this.publishedFileSignatures = [];
    this.savedPayload = structuredClone(this.editorPayloadValue);
    this.savedBaseline = this.snapshotFor(this.savedPayload, this.savedFileSignatures);
    this.publishedBaseline = this.snapshotFor(this.publishedPayloadValue, this.publishedFileSignatures);
    this.savedSlug = this.hasSlugInputTarget ? this.slugInputTarget.value : "";
    this.pendingSlug = null;
    this.slugRequestPending = false;
    this.itemFallbackThumbnailMarkup = this.itemTemplateTarget.content
      ?.querySelector("[data-editor-item-thumbnail]")?.innerHTML || "";
    this.originalPreviewHero = this.previewRoot?.querySelector(`.${PAGE_CLASS}-hero`)?.innerHTML || "";
    this.mobile = new MobileEditor(this);
    this.bindKeyboardViewport();
    this.element.querySelectorAll("[data-editor-field='name']").forEach((field) => this.bindCategoryNameField(field));
    this.updateSectionMenus();
    this.updateItemMenus();
    this.syncTargetAvailability();
    this.refreshPreview();
    this.updateControls();
    this.previewResizeObserver = new ResizeObserver(([entry]) => {
      // Render a 1440px desktop viewport at the available laptop screen size.
      this.previewScreenTarget.style.setProperty("--lib-desktop-preview-scale", entry.contentRect.width / 1440);
    });
    this.previewResizeObserver.observe(this.previewScreenTarget);
  }

  beforeCache() {
    this.mobile?.resetSettingsSlugEditor();
    this.mobile?.detailSheet?.element.close();
    this.mobile?.detailSheet?.restore();
    this.mobile?.sheet?.element.close();
    this.mobile?.sheet?.restore();
    this.mobile?.discardPendingSection();
    this.setEditorLocked(false);
    this.element.inert = false;
    this.initiallyLiveValue = this.hasPublished;
  }

  disconnect() {
    this.unbindKeyboardViewport();
    this.mobile?.disconnect();
    this.previewResizeObserver?.disconnect();
    window.cancelAnimationFrame(this.previewFrameRequest);
    window.clearTimeout(this.autosaveTimer);
    window.clearTimeout(this.saveErrorTimer);
    this.thumbnailObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    if (this.profileObjectUrl) URL.revokeObjectURL(this.profileObjectUrl);
  }

  mobilePreview() {
    this.mobile.togglePreview();
  }

  submit(event) {
    event.preventDefault();
    const intent = event.submitter?.dataset.saveIntent;
    if (!intent) return;
    this.persist(intent);
  }

  editorChanged(event) {
    const target = event.target;
    if (target?.dataset.previewOnly === "true") return;
    if (target?.closest("[data-dialog-fields]") && !target.closest("[data-editor-item]")) return;

    const item = target?.closest("[data-editor-item]");
    if (item) this.syncItemSummary(item);
    if (target?.dataset.editorField === "display_name") this.syncEditorInitials(target.value);
    if (target?.dataset.editorField === "name") this.resizeCategoryName(target);
    this.clearFieldError(target);
    if (!event.detail?.mobileInline) {
      this.queuePreviewRefresh(target?.dataset.editorField === "bio" ? "bio" : "all");
    }
    this.updateControls();
  }

  beginSlugEdit(event) {
    event?.preventDefault();
    const openedMobileSheet = this.mobile?.openSlugEditor(event?.currentTarget);
    this.slugDisplayTarget.hidden = true;
    this.slugEditorTarget.hidden = false;
    this.hideSlugError();
    this.updateSlugApplyButton();
    if (openedMobileSheet) return;

    requestAnimationFrame(() => {
      this.slugInputTarget.focus();
      this.slugInputTarget.select();
    });
  }

  bindKeyboardViewport() {
    this.onDialogFocusIn = (event) => {
      if (!this.mobile?.query.matches || !this.isTextEntry(event.target)) return;
      const dialog = event.target.closest("dialog");
      if (!dialog) return;

      if (this.keyboardDialog && this.keyboardDialog !== dialog) this.clearKeyboardDialog();
      this.keyboardDialog = dialog;
      this.scheduleKeyboardDialogSync();
    };
    this.onDialogFocusOut = (event) => {
      const dialog = event.target.closest?.("dialog");
      if (!dialog || dialog !== this.keyboardDialog) return;

      window.cancelAnimationFrame(this.keyboardFocusFrame);
      this.keyboardFocusFrame = window.requestAnimationFrame(() => {
        if (dialog.contains(document.activeElement) && this.isTextEntry(document.activeElement)) return;
        this.clearKeyboardDialog(dialog);
      });
    };
    this.onVisualViewportChange = () => this.scheduleKeyboardDialogSync();

    this.element.addEventListener("focusin", this.onDialogFocusIn);
    this.element.addEventListener("focusout", this.onDialogFocusOut);
    window.addEventListener("resize", this.onVisualViewportChange);
    window.visualViewport?.addEventListener("resize", this.onVisualViewportChange);
    window.visualViewport?.addEventListener("scroll", this.onVisualViewportChange);
  }

  unbindKeyboardViewport() {
    this.element.removeEventListener("focusin", this.onDialogFocusIn);
    this.element.removeEventListener("focusout", this.onDialogFocusOut);
    window.removeEventListener("resize", this.onVisualViewportChange);
    window.visualViewport?.removeEventListener("resize", this.onVisualViewportChange);
    window.visualViewport?.removeEventListener("scroll", this.onVisualViewportChange);
    window.cancelAnimationFrame(this.keyboardFocusFrame);
    window.cancelAnimationFrame(this.keyboardViewportFrame);
    window.cancelAnimationFrame(this.keyboardViewportMeasureFrame);
    this.element.querySelectorAll("dialog[data-link-in-bio-keyboard-docked]").forEach((dialog) => {
      this.clearKeyboardDialog(dialog);
    });
  }

  isTextEntry(element) {
    if (element instanceof HTMLTextAreaElement || element?.isContentEditable) return true;
    return element instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(element.type);
  }

  scheduleKeyboardDialogSync() {
    if (!this.keyboardDialog) return;
    window.cancelAnimationFrame(this.keyboardViewportFrame);
    window.cancelAnimationFrame(this.keyboardViewportMeasureFrame);
    this.keyboardViewportFrame = window.requestAnimationFrame(() => {
      this.keyboardViewportMeasureFrame = window.requestAnimationFrame(() => this.syncKeyboardDialog());
    });
  }

  syncKeyboardDialog() {
    const dialog = this.keyboardDialog;
    const viewport = window.visualViewport;
    if (!dialog?.open || !this.mobile?.query.matches || !viewport?.height) {
      this.clearKeyboardDialog(dialog);
      return;
    }

    const viewportBottom = viewport.offsetTop + viewport.height;
    const occludedHeight = Math.max(0, window.innerHeight - viewportBottom);
    if (occludedHeight < KEYBOARD_OCCLUSION_THRESHOLD) {
      this.undockKeyboardDialog(dialog);
      return;
    }

    dialog.setAttribute("data-link-in-bio-keyboard-docked", "");
    const viewportBottomValue = `${viewportBottom}px`;
    // Native dialogs remain content-sized, so cap overflow before aligning the rendered bottom to the keyboard edge.
    dialog.style.setProperty("max-height", viewportBottomValue);
    dialog.style.setProperty("inset", "0 auto auto 0");
    dialog.style.setProperty("margin", "0");
    const dialogHeight = dialog.getBoundingClientRect().height;
    dialog.style.setProperty("translate", `0 ${Math.max(0, viewportBottom - dialogHeight)}px`);
  }

  undockKeyboardDialog(dialog) {
    dialog?.removeAttribute("data-link-in-bio-keyboard-docked");
    KEYBOARD_DIALOG_STYLE_PROPERTIES.forEach((property) => dialog?.style.removeProperty(property));
  }

  clearKeyboardDialog(dialog = this.keyboardDialog) {
    this.undockKeyboardDialog(dialog);
    if (this.keyboardDialog === dialog) this.keyboardDialog = null;
  }

  async cancelSlugEdit(event, { restoreFocus = true } = {}) {
    event?.preventDefault();
    const mobileSheetOpen = this.mobile?.slugEditorOpen;
    this.slugInputTarget.value = this.savedSlug;
    this.finishSlugEdit({ restoreFocus: restoreFocus && !mobileSheetOpen });
    if (mobileSheetOpen) await this.mobile.closeSlugEditor();
  }

  slugInputChanged() {
    this.slugInputTarget.setCustomValidity("");
    this.hideSlugError();
    this.updateSlugApplyButton();
  }

  slugKeydown(event) {
    if (event.key === "Escape") {
      this.cancelSlugEdit(event);
    } else if (event.key === "Enter" && !event.isComposing) {
      event.preventDefault();
      this.applySlugEdit(event);
    }
  }

  async applySlugEdit(event) {
    event?.preventDefault();
    if (this.slugRequestPending) return;

    const candidate = this.slugInputTarget.value;
    this.slugInputTarget.setCustomValidity("");
    if (!this.slugInputTarget.checkValidity()) {
      this.showSlugError(this.slugMessageFor([ this.slugInputTarget.validity.valueMissing ? "blank" : "invalid" ]));
      this.slugInputTarget.focus();
      return;
    }
    if (candidate === this.savedSlug) {
      return;
    }

    this.slugConfirmationReturnFocus = event?.currentTarget || this.slugInputTarget;
    this.setSlugBusy(true);
    try {
      const url = new URL(this.slugAvailabilityUrlValue, window.location.origin);
      url.searchParams.set("link_in_bio_editor[slug]", candidate);
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const result = await this.readJson(response);
      if (!response.ok || !result.ok) {
        this.showSlugError(this.slugMessageForResult(result));
        this.slugInputTarget.focus();
        return;
      }

      if (result.slug === this.savedSlug) {
        this.slugInputTarget.value = result.slug;
        await this.cancelSlugEdit(null);
        return;
      }

      this.pendingSlug = result;
      if (!this.mobile?.slugEditorOpen) {
        this.setSlugBusy(false);
        await this.confirmSlugChange();
        return;
      }
      const description = this.slugConfirmDialogTarget.querySelector("[id$='-description']");
      if (description) description.textContent = this.slugConfirmationMessageValue.replace("%{url}", result.public_url);
      this.slugConfirmDialogTarget.showModal();
      requestAnimationFrame(() => this.slugConfirmDialogTarget.querySelector("button")?.focus());
    } catch (_error) {
      this.showSlugError(this.slugErrorsValue.network_error || this.networkErrorMessageValue);
      this.slugInputTarget.focus();
    } finally {
      this.setSlugBusy(false);
    }
  }

  cancelSlugConfirmation() {
    this.pendingSlug = null;
    requestAnimationFrame(() => this.slugConfirmationReturnFocus?.focus());
  }

  async confirmSlugChange(event) {
    event?.preventDefault();
    if (!this.pendingSlug || this.slugRequestPending) return;

    const candidate = this.pendingSlug.slug;
    this.setSlugBusy(true);
    try {
      const body = new FormData();
      body.append("link_in_bio_editor[slug]", candidate);
      const response = await fetch(this.slugUpdateUrlValue, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        credentials: "same-origin",
        body,
      });
      const result = await this.readJson(response);
      if (!response.ok || !result.ok) {
        if (this.slugConfirmDialogTarget.open) this.slugConfirmDialogTarget.close();
        this.pendingSlug = null;
        this.showSlugError(this.slugMessageForResult(result));
        requestAnimationFrame(() => this.slugInputTarget.focus());
        return;
      }

      this.savedSlug = result.slug;
      this.slugInputTarget.value = result.slug;
      this.slugValueTarget.textContent = result.slug;
      this.updatePublicUrl(result.public_url);
      this.updateSlugLock(result.slug_change_locked, result.slug_change_remaining_days);
      if (this.slugConfirmDialogTarget.open) this.slugConfirmDialogTarget.close();
      this.pendingSlug = null;
      const mobileSheetOpen = this.mobile?.slugEditorOpen;
      this.finishSlugEdit({ restoreFocus: !mobileSheetOpen });
      if (mobileSheetOpen) await this.mobile.closeSlugEditor();
    } catch (_error) {
      if (this.slugConfirmDialogTarget.open) this.slugConfirmDialogTarget.close();
      this.pendingSlug = null;
      this.showSlugError(this.slugErrorsValue.network_error || this.networkErrorMessageValue);
      requestAnimationFrame(() => this.slugInputTarget.focus());
    } finally {
      this.setSlugBusy(false);
    }
  }

  finishSlugEdit({ restoreFocus = true } = {}) {
    this.hideSlugError();
    this.slugEditorTarget.hidden = true;
    this.slugDisplayTarget.hidden = false;
    this.updateSlugApplyButton();
    if (restoreFocus) {
      const focusTarget = this.slugChangeButtonTarget.hidden ? this.slugDisplayTarget : this.slugChangeButtonTarget;
      requestAnimationFrame(() => focusTarget.focus());
    }
  }

  updateSlugLock(locked, remainingDays) {
    this.slugDisplayTarget.classList.toggle("lib-editor__url-display--locked", locked);
    this.slugChangeButtonTarget.hidden = locked;
    this.slugLockIconTarget.hidden = !locked;
    if (!locked) {
      this.slugDisplayTarget.removeAttribute("role");
      this.slugDisplayTarget.removeAttribute("tabindex");
      this.slugDisplayTarget.removeAttribute("aria-disabled");
      this.slugDisplayTarget.removeAttribute("aria-label");
      return;
    }

    const template = remainingDays === 1 ? this.slugCooldownLabelsValue.one : this.slugCooldownLabelsValue.other;
    const label = template.replace("%{count}", remainingDays);
    const prefix = this.slugDisplayTarget.querySelector(".lib-editor__url-prefix")?.textContent || "";
    this.slugDisplayTarget.setAttribute("role", "group");
    this.slugDisplayTarget.setAttribute("tabindex", "-1");
    this.slugDisplayTarget.setAttribute("aria-disabled", "true");
    this.slugDisplayTarget.setAttribute("aria-label", `${prefix}${this.slugValueTarget.textContent}. ${label}`);
  }

  updateSlugApplyButton() {
    if (!this.hasSlugApplyButtonTarget || !this.hasSlugInputTarget) return;
    this.slugApplyButtonTarget.disabled = this.slugRequestPending || this.slugInputTarget.value === this.savedSlug;
  }

  setSlugBusy(busy) {
    this.slugRequestPending = busy;
    this.slugEditorTarget.setAttribute("aria-busy", busy.toString());
    this.slugEditorTarget.querySelectorAll("input, button").forEach((control) => { control.disabled = busy; });
    this.slugConfirmDialogTarget.querySelectorAll("button").forEach((control) => { control.disabled = busy; });
    if (!busy) this.updateSlugApplyButton();
  }

  showSlugError(message) {
    this.slugErrorTarget.textContent = message;
    this.slugErrorTarget.hidden = false;
    this.slugInputTarget.setAttribute("aria-invalid", "true");
  }

  hideSlugError() {
    this.slugErrorTarget.textContent = "";
    this.slugErrorTarget.hidden = true;
    this.slugInputTarget.removeAttribute("aria-invalid");
  }

  slugMessageForResult(result) {
    const codes = [ ...(result?.errors?.value || []), ...(result?.errors?.slug || []), ...(result?.errors?.base || []) ];
    return this.slugMessageFor(codes);
  }

  slugMessageFor(codes) {
    return codes.map((code) => this.slugErrorsValue[code]).find(Boolean)
      || this.slugErrorsValue.invalid
      || this.invalidMessageValue;
  }

  updatePublicUrl(publicUrl) {
    if (!publicUrl) return;
    this.publicUrlValue = publicUrl;
    if (this.hasPublicLinkTarget && this.hasPublished) this.publicLinkTarget.href = publicUrl;

    this.element.querySelectorAll('[data-controller~="share-link"]').forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(element, "share-link");
      if (controller) controller.urlValue = publicUrl;
      element.querySelectorAll("input[readonly]").forEach((input) => { input.value = publicUrl; });
    });
    const liveDialog = document.getElementById("link-in-bio-live-dialog");
    const liveController = liveDialog && this.application.getControllerForElementAndIdentifier(liveDialog, "link-in-bio-editor-live-dialog");
    if (liveController) liveController.urlValue = publicUrl;
    liveDialog?.querySelectorAll("[data-live-page-url]").forEach((link) => {
      link.href = publicUrl;
      link.textContent = publicUrl;
    });
  }

  bindCategoryNameField(field) {
    if (!field || field.dataset.categoryNameBound === "true") return;
    field.dataset.categoryNameBound = "true";
    field.addEventListener("input", () => this.resizeCategoryName(field));
    field.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.isComposing) {
        event.preventDefault();
        field.blur();
      }
    });
    this.resizeCategoryName(field);
  }

  resizeCategoryName(field) {
    if (field?.tagName !== "TEXTAREA") return;
    field.style.height = "auto";
    field.style.height = `${field.scrollHeight}px`;
  }

  socialChanged() {
    this.queuePreviewRefresh();
    this.updateControls();
  }

  sortableChanged() {
    this.updateSectionMenus();
    this.updateItemMenus();
    this.queuePreviewRefresh();
    this.updateControls();
  }

  switchPreviewDevice(event) {
    const device = event.currentTarget.value;
    this.previewFrameTarget.classList.toggle("lib-editor__preview-frame--desktop", device === "desktop");
    this.previewFrameTarget.classList.toggle("lib-editor__preview-frame--mobile", device !== "desktop");
  }

  createSection(event, { provisional = false } = {}) {
    if (this.activeSections.length >= this.maxSectionsValue) {
      this.sectionLimitReturnFocus = event.currentTarget;
      this.sectionLimitDialogTarget.showModal();
      return null;
    }

    const clientKey = this.newClientKey("section");
    const section = this.cloneTemplate(this.sectionTemplateTarget, this.sectionTemplateKeyValue, clientKey);
    const nameField = section.querySelector("[data-editor-field='name']");
    this.bindCategoryNameField(nameField);
    if (provisional) section.dataset.provisional = "true";
    const addButton = this.sectionsList.querySelector(".lib-editor__add-section");
    this.sectionsList.insertBefore(section, addButton);
    this.syncTargetAvailability();
    this.updateSectionMenus();
    this.updateItemMenus();
    this.queuePreviewRefresh();
    this.updateControls();
    requestAnimationFrame(() => nameField?.focus());
    return section;
  }

  commitProvisionalSection(section) {
    if (!section?.matches("[data-provisional]")) return;
    delete section.dataset.provisional;
    this.syncTargetAvailability();
    this.updateSectionMenus();
    this.updateItemMenus();
    this.queuePreviewRefresh();
    this.updateControls();
  }

  discardProvisionalSection(section) {
    if (!section?.matches("[data-provisional]")) return;
    section.remove();
    this.syncTargetAvailability();
    this.updateSectionMenus();
    this.updateItemMenus();
    this.queuePreviewRefresh();
    this.updateControls();
  }

  restoreSectionLimitFocus() {
    requestAnimationFrame(() => this.sectionLimitReturnFocus?.focus());
  }

  moveSectionUp(event) {
    const section = event.currentTarget.closest("[data-editor-section]");
    const previous = this.editorSections[this.editorSections.indexOf(section) - 1];
    if (!previous) return;
    section.parentElement.insertBefore(section, previous);
    this.afterSectionMove(section);
  }

  moveSectionDown(event) {
    const section = event.currentTarget.closest("[data-editor-section]");
    const next = this.editorSections[this.editorSections.indexOf(section) + 1];
    if (!next) return;
    section.parentElement.insertBefore(next, section);
    this.afterSectionMove(section);
  }

  confirmSectionDelete(event) {
    this.sectionPendingDelete = event.currentTarget.closest("[data-editor-section]");
    this.deleteReturnFocus = this.menuButtonFor(event.currentTarget) || event.currentTarget;
    const sections = this.activeSections;
    const index = sections.indexOf(this.sectionPendingDelete);
    const survivor = sections[index + 1] || sections[index - 1];
    this.sectionDeleteFallbackFocus = survivor?.querySelector("[data-link-in-bio-editor-options-menu-target~='button']")
      || this.sectionsList.querySelector(".lib-editor__add-section");
    this.deleteDialogTarget.showModal();
  }

  cancelSectionDelete() {
    this.sectionPendingDelete = null;
    requestAnimationFrame(() => this.deleteReturnFocus?.focus());
  }

  deleteSection() {
    const section = this.sectionPendingDelete;
    if (!section) return;
    if (section.dataset.sectionId) this.deletedSectionIds.add(this.numericId(section.dataset.sectionId));
    section.querySelectorAll("[data-editor-item]").forEach((item) => this.stageItemDeletion(item));
    section.remove();
    this.sectionPendingDelete = null;
    this.deleteDialogTarget.close();
    this.updateSectionMenus();
    this.updateItemMenus();
    this.syncTargetAvailability();
    this.queuePreviewRefresh();
    this.updateControls();
    requestAnimationFrame(() => this.sectionDeleteFallbackFocus?.focus());
  }

  moveItemUp(event) {
    const item = event.currentTarget.closest("[data-editor-item]");
    const items = this.itemsInSection(item?.closest("[data-editor-section]"));
    const previous = items[items.indexOf(item) - 1];
    if (!item || !previous) return;
    item.parentElement.insertBefore(item, previous);
    this.afterItemMove(item);
  }

  moveItemDown(event) {
    const item = event.currentTarget.closest("[data-editor-item]");
    const items = this.itemsInSection(item?.closest("[data-editor-section]"));
    const next = items[items.indexOf(item) + 1];
    if (!item || !next) return;
    item.parentElement.insertBefore(next, item);
    this.afterItemMove(item);
  }

  confirmItemDelete(event) {
    this.itemPendingDelete = event.currentTarget.closest("[data-editor-item]");
    this.itemDeleteReturnFocus = this.menuButtonFor(event.currentTarget) || event.currentTarget;
    const section = this.itemPendingDelete?.closest("[data-editor-section]");
    const items = this.itemsInSection(section);
    const index = items.indexOf(this.itemPendingDelete);
    const survivor = items[index + 1] || items[index - 1];
    this.itemDeleteFallbackFocus = survivor?.querySelector("[data-link-in-bio-editor-options-menu-target~='button']")
      || section?.querySelector("[data-action*='#open']");
    this.itemDeleteDialogTarget.showModal();
  }

  cancelItemDelete() {
    this.itemPendingDelete = null;
    requestAnimationFrame(() => this.itemDeleteReturnFocus?.focus());
  }

  deleteItem() {
    const item = this.itemPendingDelete;
    if (!item) return;
    this.stageItemDeletion(item);
    item.remove();
    this.itemPendingDelete = null;
    this.itemDeleteDialogTarget.close();
    this.updateItemMenus();
    this.syncTargetAvailability();
    this.queuePreviewRefresh();
    this.updateControls();
    requestAnimationFrame(() => this.itemDeleteFallbackFocus?.focus());
  }

  beginItemEdit(event) {
    const item = event.currentTarget.closest("[data-editor-item]");
    if (!item.querySelector("[data-link-in-bio-editor-item-target~='editForm']").hidden) return;
    const pendingFile = this.pendingThumbnailFiles.get(item.dataset.clientKey);
    this.itemEditSnapshots.set(item, {
      payload: this.serializeItem(item),
      fields: [...item.querySelectorAll("[data-editor-item-field]")].map((field) => [field, field.value]),
      pendingFile,
      thumbnail: this.itemThumbnailController(item).captureState(pendingFile),
    });
  }

  discardItemEdit(event) {
    const item = event.currentTarget.closest("[data-editor-item]");
    const snapshot = this.itemEditSnapshots.get(item);
    if (!snapshot) return;
    snapshot.fields.forEach(([field, value]) => { field.value = value; });
    this.clearPendingThumbnail(item.dataset.clientKey);
    if (snapshot.pendingFile) this.pendingThumbnailFiles.set(item.dataset.clientKey, snapshot.pendingFile);
    this.itemThumbnailController(item).restoreState(snapshot.thumbnail);
    this.syncItemSummary(item);
    this.queuePreviewRefresh();
    this.updateControls();
    this.closeItemEdit(event);
  }

  itemThumbnailController(item) {
    return this.application.getControllerForElementAndIdentifier(
      item.querySelector("[data-controller~='link-in-bio-editor-thumbnail']"),
      "link-in-bio-editor-thumbnail"
    );
  }

  closeItemEdit(event) {
    const item = event.currentTarget.closest("[data-editor-item]");
    this.itemEditSnapshots.delete(item);
    this.updateControls();
    const editForm = event.currentTarget.closest("[class*='item-edit-form']");
    if (editForm) editForm.hidden = true;
    item?.querySelector("[data-link-in-bio-editor-item-target~='editButton']")?.setAttribute("aria-expanded", "false");
    requestAnimationFrame(() => this.menuButtonFor(item)?.focus());
  }

  addBryghtItems(event) {
    const dialog = event.currentTarget.closest("dialog");
    const section = dialog?.closest("[data-editor-section]");
    if (!dialog || !section) return;

    const fields = event.currentTarget.closest("[data-dialog-fields]");
    const selections = [...(fields || dialog).querySelectorAll(`${TARGET_INPUT_SELECTOR}:not([disabled])`)];
    if (!this.canAddItems(section, selections.length)) return;
    selections.forEach((selection) => {
      const row = selection.closest(TARGET_ROW_SELECTOR);
      const item = this.buildItem({
        kind: "bryght",
        targetSgid: selection.value,
        targetKey: selection.dataset.targetKey,
        title: row?.querySelector("strong")?.textContent?.trim() || "",
        subtitle: row?.querySelector("small")?.textContent?.trim() || "BRYGHT",
        price: row?.dataset.previewPrice || "",
        thumbnailSource: row?.querySelector("template[data-target-preview-image]")?.content
          || selection.nextElementSibling,
      });
      section.querySelector("[data-item-list]")?.append(item);
    });

    dialog.close();
    this.updateItemMenus();
    window.dispatchEvent(new CustomEvent("link-in-bio-picker:reset"));
    this.syncTargetAvailability();
    this.queuePreviewRefresh();
    this.updateControls();
  }

  addExternalItem(event) {
    const dialog = event.currentTarget.closest("dialog");
    const section = dialog?.closest("[data-editor-section]");
    const fields = event.currentTarget.closest("[data-external-fields]");
    const titleInput = fields?.querySelector("[data-external-field='title']");
    const urlInput = fields?.querySelector("[data-external-field='url']");
    if (!dialog || !section || !fields || !titleInput || !urlInput) return;
    if (!this.canAddItems(section, 1)) return;
    if (!this.validateExternalInputs(titleInput, urlInput)) return;

    const clientKey = this.newClientKey("item");
    const item = this.buildItem({
      clientKey,
      kind: "external",
      title: titleInput.value.trim(),
      subtitle: urlInput.value.trim(),
    });
    const itemTitle = item.querySelector("[data-editor-item-field='external_title']");
    const itemUrl = item.querySelector("[data-editor-item-field='external_url']");
    if (itemTitle) itemTitle.value = titleInput.value.trim();
    if (itemUrl) itemUrl.value = urlInput.value.trim();

    const sourceThumbnail = fields.querySelector("[data-controller~='link-in-bio-editor-thumbnail']");
    const file = sourceThumbnail?.querySelector(THUMBNAIL_INPUT_SELECTOR)?.files?.[0];
    if (file) {
      this.pendingThumbnailFiles.set(clientKey, file);
      this.showPendingThumbnail(item, file, clientKey);
    }

    section.querySelector("[data-item-list]")?.append(item);
    this.updateItemMenus();
    this.resetExternalDialog(fields);
    dialog.close();
    this.queuePreviewRefresh();
    this.updateControls();
  }

  beforeUnload(event) {
    if (this.allowNavigation || !this.localDirty) return;
    event.preventDefault();
    event.returnValue = "";
  }

  async beforeVisit(event) {
    if (this.allowNavigation || !this.localDirty) return;
    event.preventDefault();
    if (this.pendingDestination) return;

    this.pendingDestination = event.detail.url;
    this.navigationFocus = document.activeElement;
    this.setEditorLocked(true);
    this.element.inert = true;
    const saved = await this.persist("draft", { showSuccess: false });
    if (saved && !this.localDirty) this.resumeNavigation();
    else {
      this.pendingDestination = null;
      this.element.inert = false;
      this.setEditorLocked(false);
      this.updateControls();
      this.navigationFocus?.focus();
    }
  }

  confirmDiscard(event) {
    window.clearTimeout(this.autosaveTimer);
    this.autosavePaused = true;
    this.discardReturnFocus = event.currentTarget;
    this.discardDialogTarget.showModal();
  }

  cancelDiscard() {
    this.autosavePaused = false;
    this.scheduleAutosave();
    requestAnimationFrame(() => this.discardReturnFocus?.focus());
  }

  async discardChanges() {
    if (this.savePromise) await this.savePromise;
    if (this.isSaving) return;
    window.clearTimeout(this.autosaveTimer);

    this.discardDialogTarget.close();
    this.setSaving(true);
    this.announce("");

    try {
      const body = new FormData();
      body.append("link_in_bio_editor[lock_version]", this.lockVersion);
      const response = await fetch(this.discardUrlValue, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        credentials: "same-origin",
        body,
      });
      const result = await this.readJson(response);
      if (!response.ok || !result.ok) {
        const message = this.discardErrorMessageValue;
        this.reportSaveError(message, "discard", { status: response.status, errors: result.errors });
        return;
      }

      this.allowNavigation = true;
      window.location.reload();
    } catch (error) {
      this.reportSaveError(this.discardErrorMessageValue, "discard", error);
    } finally {
      this.autosavePaused = false;
      this.setSaving(false);
    }
  }

  scheduleAutosave() {
    window.clearTimeout(this.autosaveTimer);
    if (!this.localDirty || this.isSaving || this.autosavePaused || !this.element.isConnected) return;
    this.autosaveTimer = window.setTimeout(() => this.persist("draft"), 900);
  }

  persist(intent, options = {}) {
    window.clearTimeout(this.autosaveTimer);
    if (this.savePromise) return this.savePromise.then((saved) => saved ? this.persist(intent, options) : false);
    if (intent === "draft" && !this.localDirty) return Promise.resolve(true);
    const request = this.performSave(intent, options);
    this.savePromise = request;
    request.finally(() => { if (this.savePromise === request) this.savePromise = null; });
    return request;
  }

  async performSave(intent, { showSuccess = true } = {}) {
    if (this.isSaving) return false;
    if (intent === "publish" && !this.validateEditor()) {
      const errors = [...this.element.querySelectorAll("input:invalid, textarea:invalid, select:invalid")].map((field) => ({
        field,
        message: field.validationMessage,
      }));
      this.reportSaveError(this.invalidMessageValue, intent, { errors });
      return false;
    }
    const payload = this.serializePayload();
    const files = this.fileSignatures();
    const snapshot = this.snapshotFor(payload, files);
    const url = intent === "publish" ? this.publishUrlValue : this.draftUrlValue;
    this.saveError = false;
    this.announce("");
    this.setSaving(true, { lockEditor: intent !== "draft" });
    let succeeded = false;

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        credentials: "same-origin",
        body: this.buildFormData(payload),
      });
      const result = await this.readJson(response);
      if (!response.ok || !result.ok) {
        this.handleSaveFailure(result, response.status, intent);
        return false;
      }

      this.applySuccessfulResponse(result, intent, { payload, files, snapshot });
      succeeded = true;
      if (showSuccess && intent === "publish") this.showPublishedDialog(result.public_url);
      return true;
    } catch (error) {
      this.reportSaveError(this.networkErrorMessageValue, intent, error);
      return false;
    } finally {
      this.setSaving(false);
      if (succeeded) this.scheduleAutosave();
    }
  }

  serializePayload() {
    const social = this.socialState;
    return {
      lock_version: this.lockVersion,
      display_name: this.fieldValue("display_name"),
      bio: this.fieldValue("bio"),
      theme: this.element.querySelector("[data-editor-field='theme']:checked")?.value || "dark",
      social_links: social.links,
      hidden_social_platforms: social.hiddenPlatforms,
      sections: this.activeSections.map((section) => ({
        client_key: section.dataset.clientKey,
        id: this.numericId(section.dataset.sectionId),
        name: section.querySelector("[data-editor-field='name']")?.value || "",
        layout: section.querySelector("[data-editor-field='layout']:checked")?.value || "list",
        items: this.itemsInSection(section).map((item) => this.serializeItem(item)),
      })),
      deleted_section_ids: [...this.deletedSectionIds],
      deleted_item_ids: [...this.deletedItemIds],
    };
  }

  serializeItem(item) {
    const accepted = this.itemEditSnapshots.get(item)?.payload;
    if (accepted) return { ...accepted, id: this.numericId(item.dataset.itemId) };
    return {
      client_key: item.dataset.clientKey,
      id: this.numericId(item.dataset.itemId),
      kind: item.dataset.itemKind,
      target_sgid: item.dataset.itemKind === "bryght" ? (item.dataset.targetSgid || null) : null,
      external_title: item.dataset.itemKind === "external" ? (item.querySelector("[data-editor-item-field='external_title']")?.value || "") : null,
      external_url: item.dataset.itemKind === "external" ? (item.querySelector("[data-editor-item-field='external_url']")?.value || "") : null,
      remove_thumbnail: item.querySelector(THUMBNAIL_REMOVAL_SELECTOR)?.value === "1",
    };
  }

  itemFile(item) {
    const accepted = this.itemEditSnapshots.get(item);
    if (accepted) return accepted.thumbnail.files[0] || accepted.pendingFile;
    return item.querySelector(THUMBNAIL_INPUT_SELECTOR)?.files?.[0]
      || this.pendingThumbnailFiles.get(item.dataset.clientKey);
  }

  buildFormData(payload) {
    const body = new FormData();
    body.append("link_in_bio_editor[payload]", JSON.stringify(payload));
    body.append("link_in_bio_editor[baseline]", JSON.stringify(this.savedPayload));
    const profileFile = this.element.querySelector("[data-link-in-bio-editor-profile-file]")?.files?.[0];
    if (profileFile && !this.savedFileSignatures.includes(`profile:${this.fileSignature(profileFile)}`)) {
      body.append("link_in_bio_editor[profile_picture]", profileFile);
    }

    this.activeSections.forEach((section) => {
      section.querySelectorAll(":scope > [data-item-list] > [data-editor-item]").forEach((item) => {
        const removal = this.serializeItem(item).remove_thumbnail;
        const file = this.itemFile(item);
        if (file && !removal && !this.savedFileSignatures.includes(`thumbnail:${item.dataset.clientKey}:${this.fileSignature(file)}`)) {
          body.append(`link_in_bio_editor[item_thumbnails][${item.dataset.clientKey}]`, file);
        }
      });
    });
    return body;
  }

  applySuccessfulResponse(result, intent, { payload, files, snapshot }) {
    const editedDuringSave = this.currentSnapshot !== snapshot;
    if (result.status) {
      this.hasPublished = result.status === "live";
      this.initiallyLiveValue = this.hasPublished;
    }
    // Preserve edits made during the request. Once typing settles, reconcile
    // the server graph so the preview and saved baseline describe the same page.
    const canonicalPayload = result.editor_payload || payload;
    // Rebuilding an unchanged graph replaces the mobile inline input being
    // edited, which makes iOS move the layout viewport despite preventScroll.
    const needsEditorReconciliation = !editedDuringSave && this.snapshotFor(canonicalPayload, files) !== snapshot;
    const editorPayload = structuredClone(editedDuringSave ? payload : canonicalPayload);
    const sections = new Map((canonicalPayload.sections || []).map((section) => [section.client_key, section]));
    const items = new Map((canonicalPayload.sections || []).flatMap((section) => section.items).map((item) => [item.client_key, item]));
    editorPayload.sections.forEach((section) => {
      section.id = sections.get(section.client_key)?.id ?? section.id;
      section.items.forEach((item) => {
        item.id = items.get(item.client_key)?.id ?? item.id;
        if (intent === "publish") item.remove_thumbnail = false;
      });
    });
    if (intent === "publish") {
      editorPayload.deleted_section_ids = [];
      editorPayload.deleted_item_ids = [];
    }
    const publishedPayload = result.published_payload || (intent === "publish" ? editorPayload : this.publishedPayloadValue);
    this.lockVersion = Number(result.lock_version ?? editorPayload.lock_version ?? this.lockVersion);
    editorPayload.lock_version = this.lockVersion;
    if (publishedPayload) publishedPayload.lock_version = this.lockVersion;
    const incoming = result.editor_html ? new DOMParser().parseFromString(result.editor_html, "text/html") : null;
    if (needsEditorReconciliation) this.reconcileEditor(editorPayload, incoming, intent);
    this.adoptCanonicalIds(editorPayload);
    if (!editedDuringSave) {
      this.reconcileThumbnailRemovalFlags(editorPayload);
      this.reconcileSocialState(editorPayload);
    }
    this.editorPayloadValue = editorPayload;
    this.publishedPayloadValue = publishedPayload;

    let currentFiles = files;
    if (intent === "draft") this.captureDraftAssetKeys(editorPayload, files);
    if (intent === "publish") {
      this.hasPublished = true;
      this.initiallyLiveValue = true;
      const hero = this.previewRoot?.querySelector(`.${PAGE_CLASS}-hero`);
      const publishedHero = incoming?.querySelector(`#link-in-bio-preview .${PAGE_CLASS}-hero`);
      if (publishedHero || hero) this.originalPreviewHero = (publishedHero || hero).innerHTML;
      this.clearCommittedFileInputs();
      this.draftAssetKeysValue = [];
      currentFiles = [];
    }
    this.savedPayload = structuredClone(editorPayload);
    this.savedFileSignatures = currentFiles;
    this.savedBaseline = this.snapshotFor(editorPayload, this.savedFileSignatures);
    if (intent === "publish") this.publishedFileSignatures = currentFiles;
    this.publishedBaseline = this.snapshotFor(publishedPayload, this.publishedFileSignatures);
    if (!editedDuringSave) {
      this.deletedSectionIds = new Set(editorPayload.deleted_section_ids || []);
      this.deletedItemIds = new Set(editorPayload.deleted_item_ids || []);
    }
  }

  reconcileEditor(payload, incoming, intent) {
    for (const field of ["display_name", "bio"]) {
      const input = this.element.querySelector(`[data-editor-field='${field}']`);
      if (input) input.value = payload[field] || "";
    }
    this.element.querySelectorAll("[data-editor-field='theme']").forEach((input) => {
      input.checked = input.value === payload.theme;
    });
    this.syncEditorInitials(payload.display_name || "");
    const sections = new Map(this.activeSections.map((section) => [section.dataset.clientKey, section]));
    const items = new Map([...this.element.querySelectorAll("[data-editor-item]")].map((item) => [item.dataset.clientKey, item]));
    const keptSections = new Set();
    const keptItems = new Set();
    for (const [sectionIndex, sectionData] of (payload.sections || []).entries()) {
      const selector = `[data-editor-section][data-client-key="${CSS.escape(sectionData.client_key)}"]`;
      const section = sections.get(sectionData.client_key) || incoming?.querySelector(selector)?.cloneNode(true);
      if (!section) continue;
      keptSections.add(sectionData.client_key);
      if (this.activeSections[sectionIndex] !== section) this.sectionsList.insertBefore(section, this.activeSections[sectionIndex] || this.sectionsList.querySelector(".lib-editor__add-section"));
      section.querySelector("[data-editor-field='name']").value = sectionData.name;
      section.querySelectorAll("[data-editor-field='layout']").forEach((input) => {
        input.checked = input.value === sectionData.layout;
      });
      const list = section.querySelector("[data-item-list]");
      for (const [itemIndex, itemData] of sectionData.items.entries()) {
        const itemSelector = `[data-editor-item][data-client-key="${CSS.escape(itemData.client_key)}"]`;
        const source = incoming?.querySelector(itemSelector);
        const item = items.get(itemData.client_key) || section.querySelector(itemSelector) || source?.cloneNode(true);
        if (!item) continue;
        keptItems.add(itemData.client_key);
        if (list.children[itemIndex] !== item) list.insertBefore(item, list.children[itemIndex] || null);
        const snapshot = this.itemEditSnapshots.get(item);
        if (snapshot) {
          snapshot.payload = structuredClone(itemData);
          snapshot.fields = snapshot.fields.map(([field]) => [field, itemData[field.dataset.editorItemField] || ""]);
          continue;
        }
        item.querySelectorAll("[data-editor-item-field]").forEach((field) => {
          field.value = itemData[field.dataset.editorItemField] || "";
        });
        if (source && item !== source) {
          if (intent === "publish" || !this.itemFile(item)) {
            const thumbnail = item.querySelector("[data-controller~='link-in-bio-editor-thumbnail']");
            const freshThumbnail = source.querySelector("[data-controller~='link-in-bio-editor-thumbnail']");
            if (thumbnail && freshThumbnail) thumbnail.replaceWith(freshThumbnail.cloneNode(true));
          }
          const template = item.querySelector("template[data-editor-item-preview-thumbnail]");
          const freshTemplate = source.querySelector("template[data-editor-item-preview-thumbnail]");
          if (template && freshTemplate) template.innerHTML = freshTemplate.innerHTML;
          else if (template) template.innerHTML = "";
          else if (freshTemplate) item.append(freshTemplate.cloneNode(true));
        }
        this.syncItemSummary(item);
      }
    }
    items.forEach((item, key) => { if (!keptItems.has(key)) item.remove(); });
    sections.forEach((section, key) => { if (!keptSections.has(key)) section.remove(); });
    this.updateSectionMenus();
    this.updateItemMenus();
    this.syncTargetAvailability({ preserveSelection: true });
    this.queuePreviewRefresh();
  }

  adoptCanonicalIds(payload) {
    (payload.sections || []).forEach((sectionData) => {
      const section = this.element.querySelector(`[data-editor-section][data-client-key="${CSS.escape(sectionData.client_key)}"]`);
      if (!section) return;
      section.dataset.sectionId = sectionData.id || "";
      (sectionData.items || []).forEach((itemData) => {
        const item = section.querySelector(`[data-editor-item][data-client-key="${CSS.escape(itemData.client_key)}"]`);
        if (item) {
          item.dataset.itemId = itemData.id || "";
          if (itemData.target_sgid) item.dataset.targetSgid = itemData.target_sgid;
        }
      });
    });
  }

  reconcileThumbnailRemovalFlags(payload) {
    (payload.sections || []).forEach((sectionData) => {
      (sectionData.items || []).forEach((itemData) => {
        const item = this.element.querySelector(`[data-editor-item][data-client-key="${CSS.escape(itemData.client_key)}"]`);
        const removal = item?.querySelector(THUMBNAIL_REMOVAL_SELECTOR);
        if (item && this.itemEditSnapshots.has(item)) return;
        if (removal) removal.value = itemData.remove_thumbnail ? "1" : "0";
      });
    });
  }

  reconcileSocialState(payload) {
    const root = this.element.querySelector("[data-controller~='link-in-bio-editor-social-controls']");
    const controller = root && this.application.getControllerForElementAndIdentifier(
      root,
      "link-in-bio-editor-social-controls"
    );
    controller?.replaceState(payload.social_links || {}, payload.hidden_social_platforms || []);
  }

  validateEditor() {
    const name = this.element.querySelector("[data-editor-field='display_name']");
    if (name && !name.value.trim()) {
      name.setCustomValidity(this.invalidMessageValue);
      name.reportValidity();
      name.focus();
      return false;
    }
    name?.setCustomValidity("");

    const sectionNames = [...this.element.querySelectorAll("[data-editor-section] [data-editor-field='name']")];
    sectionNames.forEach((sectionName) => sectionName.setCustomValidity(""));
    const blankSectionName = sectionNames.find((sectionName) => !sectionName.value.trim());
    if (blankSectionName) {
      blankSectionName.setCustomValidity(this.invalidMessageValue);
      blankSectionName.reportValidity();
      blankSectionName.focus();
      return false;
    }

    for (const item of this.element.querySelectorAll("[data-editor-item][data-item-kind='external']")) {
      if (this.itemEditSnapshots.has(item)) continue;
      const title = item.querySelector("[data-editor-item-field='external_title']");
      const url = item.querySelector("[data-editor-item-field='external_url']");
      if (!this.validateExternalInputs(title, url)) {
        this.beginItemEdit({ currentTarget: item });
        const editForm = item.querySelector("[class*='item-edit-form']");
        if (editForm) editForm.hidden = false;
        item.querySelector("[data-link-in-bio-editor-item-target~='editButton']")?.setAttribute("aria-expanded", "true");
        (title.value.trim() ? url : title).focus();
        return false;
      }
    }
    return true;
  }

  validateExternalInputs(title, url) {
    if (!title || !url) return false;
    title.setCustomValidity(title.value.trim() ? "" : this.invalidMessageValue);
    let validUrl = false;
    try {
      const parsed = new URL(url.value.trim());
      validUrl = parsed.protocol === "https:" && Boolean(parsed.hostname);
    } catch (_error) {
      validUrl = false;
    }
    url.setCustomValidity(validUrl ? "" : (this.invalidUrlMessageValue || this.invalidMessageValue));
    if (!title.checkValidity()) {
      title.reportValidity();
      return false;
    }
    if (!url.checkValidity()) {
      url.reportValidity();
      return false;
    }
    return true;
  }

  handleSaveFailure(result, status, intent) {
    const message = result.errors?.sections?.includes("conflict") ? this.conflictMessageValue
      : Object.values(result.errors || {}).some((errors) => errors.includes("too_many")) ? this.itemLimitMessageValue
      : status >= 500 || result.errors?.response ? this.networkErrorMessageValue : this.invalidMessageValue;
    if (result.status) this.hasPublished = result.status === "live";
    this.reportSaveError(message, intent, { status, errors: result.errors });
  }

  refreshPreview(mode = "all") {
    const root = this.previewRoot;
    if (!root) return;

    // Bio edits do not affect the product links. Updating the text in place keeps
    // their image elements alive instead of restarting their image loads.
    if (mode === "bio") {
      this.refreshPreviewBio(root, this.fieldValue("bio"));
      return;
    }

    const focusedField = this.mobile?.captureInlineFocus(root);
    const scrollPosition = this.mobile?.capturePageScrollPosition();
    const name = this.fieldValue("display_name");
    const bio = this.fieldValue("bio");
    const nameNode = root.querySelector(`.${PAGE_CLASS}-name`);
    if (nameNode) nameNode.textContent = name;
    this.refreshPreviewBio(root, bio);
    this.refreshPreviewTheme(root);
    this.refreshPreviewProfile(root, name);
    this.refreshPreviewSocials(root);
    this.refreshPreviewSections(root);
    this.mobile?.decorate(root);
    this.mobile?.restoreInlineFocus(root, focusedField);
    this.mobile?.restorePageScrollPosition(scrollPosition);
  }

  refreshPreviewBio(root, bio) {
    const profile = root.querySelector(`.${PAGE_CLASS}-profile`);
    let bioNode = root.querySelector(`.${PAGE_CLASS}-bio`);
    if (!bio) {
      bioNode?.remove();
      return;
    }
    if (!bioNode && profile) {
      bioNode = document.createElement("p");
      bioNode.className = `${PAGE_CLASS}-bio`;
      profile.querySelector(`.${PAGE_CLASS}-name`)?.after(bioNode);
    }
    if (bioNode) bioNode.textContent = bio;
  }

  refreshPreviewTheme(root) {
    const theme = this.element.querySelector("[data-editor-field='theme']:checked")?.value || "dark";
    root.classList.toggle(`${PAGE_CLASS}-root--dark`, theme === "dark");
    root.classList.toggle(`${PAGE_CLASS}-root--light`, theme === "light");
  }

  refreshPreviewProfile(root, name) {
    const hero = root.querySelector(`.${PAGE_CLASS}-hero`);
    if (!hero) return;
    const file = this.element.querySelector("[data-link-in-bio-editor-profile-file]")?.files?.[0];
    if (file) {
      if (this.profileFile !== file) {
        if (this.profileObjectUrl) URL.revokeObjectURL(this.profileObjectUrl);
        this.profileFile = file;
        this.profileObjectUrl = URL.createObjectURL(file);
      }
      const picture = document.createElement("span");
      picture.className = `${PAGE_CLASS}-hero-picture`;
      const responsivePicture = document.createElement("picture");
      const image = document.createElement("img");
      image.alt = name;
      image.src = this.profileObjectUrl;
      responsivePicture.append(image);
      picture.append(responsivePicture);
      hero.replaceChildren(picture);
      this.setHeroPictureOrientation(picture);
      return;
    }
    if (this.draftAssetKeysValue.includes("profile_picture")) {
      const profileEditor = this.element.querySelector("[data-controller~='link-in-bio-editor-profile-picture']");
      const previewTemplate = profileEditor?.querySelector("template[data-editor-profile-preview-image]");
      const editorImage = previewTemplate?.content.querySelector("picture, img")
        || profileEditor?.querySelector("img");
      if (editorImage) {
        const picture = document.createElement("span");
        picture.className = `${PAGE_CLASS}-hero-picture`;
        picture.append(editorImage.cloneNode(true));
        hero.replaceChildren(picture);
        this.setHeroPictureOrientation(picture);
        return;
      }
    }
    if (this.originalPreviewHero) {
      hero.innerHTML = this.originalPreviewHero;
      const image = hero.querySelector("img");
      if (image) image.alt = name;
      const initials = hero.querySelector(`.${PAGE_CLASS}-initials`);
      if (initials) initials.textContent = this.initialsFor(name);
      this.setHeroPictureOrientation(hero.querySelector(`.${PAGE_CLASS}-hero-picture`));
      return;
    }
    const initials = document.createElement("span");
    initials.className = `${PAGE_CLASS}-initials`;
    initials.setAttribute("aria-hidden", "true");
    initials.textContent = this.initialsFor(name);
    hero.replaceChildren(initials);
  }

  refreshPreviewSocials(root) {
    const profile = root.querySelector(`.${PAGE_CLASS}-profile`);
    if (!profile) return;
    profile.querySelector(".c-link_in_bio--social_links-links")?.remove();
    const { links, hiddenPlatforms } = this.socialState;
    const visible = Object.keys(links).filter((platform) => links[platform] && !hiddenPlatforms.includes(platform));
    if (!visible.length) return;

    const list = document.createElement("div");
    list.className = "c-link_in_bio--social_links-links";
    visible.forEach((platform) => {
      const source = this.element.querySelector(`[data-social-editor-trigger][data-platform="${CSS.escape(platform)}"]`);
      if (!source) return;

      const link = document.createElement("a");
      link.className = "c-link_in_bio--social_links-links__item";
      if (platform === "email") link.href = `mailto:${links[platform].replace(/^mailto:/i, "")}`;
      else if (platform === "generic_link" && !/^https?:\/\//i.test(links[platform])) link.href = `https://${links[platform]}`;
      else link.href = links[platform];
      link.tabIndex = -1;
      const icon = source.querySelector("svg")?.cloneNode(true);
      if (icon) {
        icon.classList.add("c-link_in_bio--social_links-links__icon");
        link.append(icon);
      }

      list.append(link);
    });
    profile.append(list);
  }

  refreshPreviewSections(root) {
    const content = root.querySelector(`.${PAGE_CLASS}-content`);
    if (!content) return;
    const fragment = document.createDocumentFragment();
    this.activeSections.forEach((section) => {
      const preview = this.previewSection(section);
      if (preview) fragment.append(preview);
    });
    content.replaceChildren(fragment);
  }

  previewSection(section) {
    const editorItems = [...section.querySelectorAll(":scope > [data-item-list] > [data-editor-item]")]
      .filter((item) => item.dataset.itemUnavailable !== "true");
    if (!editorItems.length && (!this.mobile?.query.matches || this.mobile.previewing)) return null;

    const node = document.createElement("section");
    node.className = `${PAGE_CLASS}-section`;
    node.dataset.linkInBioPreviewSectionId = section.dataset.clientKey;
    const name = section.querySelector("[data-editor-field='name']")?.value.trim();
    if (name) {
      const heading = document.createElement("h2");
      heading.className = `${PAGE_CLASS}-section-title`;
      heading.textContent = name;
      node.append(heading);
    }

    const layout = section.querySelector("[data-editor-field='layout']:checked")?.value || "list";
    const items = document.createElement("div");
    items.className = `${PAGE_CLASS}-items ${PAGE_CLASS}-items--${layout === "grid" ? "row" : "list"}`;
    items.dataset.linkInBioPreviewItemsSectionId = section.dataset.clientKey;
    editorItems.forEach((item) => {
      items.append(this.previewItem(item));
    });
    node.append(items);
    return node;
  }

  previewItem(item) {
    const link = document.createElement("a");
    link.className = `${PAGE_CLASS}-item`;
    link.href = item.dataset.itemKind === "external"
      ? (item.querySelector("[data-editor-item-field='external_url']")?.value || "#")
      : "#";
    link.tabIndex = -1;
    link.dataset.linkInBioPreviewItemId = item.dataset.clientKey;

    const tile = document.createElement("span");
    tile.className = `${PAGE_CLASS}-tile`;
    const thumbnail = item.querySelector("[data-editor-item-thumbnail]");
    const removal = item.querySelector(THUMBNAIL_REMOVAL_SELECTOR)?.value === "1";
    const selectedImage = item.querySelector("[data-link-in-bio-editor-thumbnail-target~='selectedPreview']:not([hidden])");
    const previewTemplate = item.querySelector("template[data-editor-item-preview-thumbnail]");
    const imageSource = removal ? null : (
      selectedImage
      || previewTemplate?.content.querySelector("picture, img")
      || thumbnail?.querySelector("picture, img")
    );
    if (imageSource) {
      const picture = document.createElement("span");
      picture.className = `${PAGE_CLASS}-tile-picture`;
      picture.append(imageSource.cloneNode(true));
      tile.append(picture);
    } else {
      const fallback = document.createElement("span");
      fallback.className = `${PAGE_CLASS}-tile-fallback`;
      tile.append(fallback);
      const icon = thumbnail?.querySelector("svg")?.cloneNode(true);
      if (icon) {
        icon.classList.add(`${PAGE_CLASS}-type-icon`);
        tile.append(icon);
      }
    }

    const caption = document.createElement("span");
    caption.className = `${PAGE_CLASS}-caption`;
    const title = document.createElement("strong");
    title.className = `${PAGE_CLASS}-item-title`;
    title.textContent = item.querySelector("[data-editor-item-title]")?.textContent || "";
    caption.append(title);
    link.append(tile, caption);
    return link;
  }

  syncItemSummary(item) {
    if (item.dataset.itemKind !== "external") return;
    const title = item.querySelector("[data-editor-item-field='external_title']")?.value || "";
    const url = item.querySelector("[data-editor-item-field='external_url']")?.value || "";
    const titleNode = item.querySelector("[data-editor-item-title]");
    const subtitleNode = item.querySelector("[data-editor-item-subtitle]");
    if (titleNode) titleNode.textContent = title;
    if (subtitleNode) subtitleNode.textContent = url;

    const thumbnailField = item.querySelector("[data-controller~='link-in-bio-editor-thumbnail']");
    const selected = thumbnailField?.querySelector("[data-link-in-bio-editor-thumbnail-target~='selectedPreview']:not([hidden])");
    const current = thumbnailField?.querySelector("[data-link-in-bio-editor-thumbnail-target~='currentPreview']:not([hidden]) picture, [data-link-in-bio-editor-thumbnail-target~='currentPreview']:not([hidden]) img");
    const source = selected || current;
    const thumbnail = item.querySelector("[data-editor-item-thumbnail]");
    if (source) {
      thumbnail?.replaceChildren(source.cloneNode(true));
    } else if (thumbnail && this.itemFallbackThumbnailMarkup) {
      thumbnail.innerHTML = this.itemFallbackThumbnailMarkup;
    }

    const removal = item.querySelector(THUMBNAIL_REMOVAL_SELECTOR);
    if (removal?.value === "1") this.clearPendingThumbnail(item.dataset.clientKey);
  }

  buildItem({ clientKey = this.newClientKey("item"), kind, targetSgid = "", targetKey = "", title, subtitle, price = "", thumbnailSource }) {
    const item = this.cloneTemplate(this.itemTemplateTarget, this.itemTemplateKeyValue, clientKey);
    item.dataset.itemKind = kind;
    item.dataset.itemUnavailable = "false";
    item.dataset.targetSgid = targetSgid;
    item.dataset.targetKey = targetKey;
    item.dataset.itemId = "";
    item.querySelector("[data-editor-item-title]").textContent = title;
    item.querySelector("[data-editor-item-subtitle]").textContent = subtitle;
    const badge = item.querySelector("[data-editor-external-badge]");
    if (badge) badge.hidden = kind !== "external";
    const priceNode = item.querySelector("[data-editor-item-price]");
    if (priceNode) {
      priceNode.textContent = price;
      priceNode.hidden = !price;
    }
    const edit = item.querySelector("[data-link-in-bio-editor-item-target~='editButton']");
    const editForm = item.querySelector("[class*='item-edit-form']");
    if (kind !== "external") {
      edit?.closest("li")?.remove();
      editForm?.remove();
      const thumbnail = item.querySelector("[data-editor-item-thumbnail]");
      if (thumbnailSource && thumbnail) thumbnail.replaceChildren(...[...thumbnailSource.childNodes].map((node) => node.cloneNode(true)));
    }
    return item;
  }

  showPendingThumbnail(item, file, clientKey) {
    const oldUrl = this.thumbnailObjectUrls.get(clientKey);
    if (oldUrl) URL.revokeObjectURL(oldUrl);
    const url = URL.createObjectURL(file);
    this.thumbnailObjectUrls.set(clientKey, url);
    const image = document.createElement("img");
    image.src = url;
    image.alt = "";
    item.querySelector("[data-editor-item-thumbnail]")?.replaceChildren(image.cloneNode(true));
    const selected = item.querySelector("[data-link-in-bio-editor-thumbnail-target~='selectedPreview']");
    if (selected) {
      selected.src = url;
      selected.hidden = false;
    }
    const placeholder = item.querySelector("[data-link-in-bio-editor-thumbnail-target~='placeholder']");
    const remove = item.querySelector("[data-link-in-bio-editor-thumbnail-target~='remove']");
    const removal = item.querySelector(THUMBNAIL_REMOVAL_SELECTOR);
    if (placeholder) placeholder.hidden = true;
    if (remove) remove.hidden = false;
    if (removal) removal.value = "0";
  }

  resetExternalDialog(fields) {
    fields.querySelectorAll("[data-external-field]").forEach((input) => {
      input.value = "";
      input.setCustomValidity("");
    });
    const thumbnail = fields.querySelector("[data-controller~='link-in-bio-editor-thumbnail']");
    const input = thumbnail?.querySelector(THUMBNAIL_INPUT_SELECTOR);
    const selected = thumbnail?.querySelector("[data-link-in-bio-editor-thumbnail-target~='selectedPreview']");
    const placeholder = thumbnail?.querySelector("[data-link-in-bio-editor-thumbnail-target~='placeholder']");
    const remove = thumbnail?.querySelector("[data-link-in-bio-editor-thumbnail-target~='remove']");
    const removal = thumbnail?.querySelector(THUMBNAIL_REMOVAL_SELECTOR);
    if (input) input.value = "";
    if (selected) {
      selected.removeAttribute("src");
      selected.hidden = true;
    }
    if (placeholder) placeholder.hidden = false;
    if (remove) remove.hidden = true;
    if (removal) removal.value = "0";
  }

  stageItemDeletion(item) {
    if (item.dataset.itemId) this.deletedItemIds.add(this.numericId(item.dataset.itemId));
    const key = item.dataset.clientKey;
    this.clearPendingThumbnail(key);
  }

  clearPendingThumbnail(key) {
    this.pendingThumbnailFiles.delete(key);
    const url = this.thumbnailObjectUrls.get(key);
    if (url) URL.revokeObjectURL(url);
    this.thumbnailObjectUrls.delete(key);
  }

  syncTargetAvailability({ preserveSelection = false } = {}) {
    const used = new Set([...this.element.querySelectorAll("[data-editor-item][data-item-kind='bryght']")]
      .map((item) => item.dataset.targetKey)
      .filter(Boolean));
    this.element.querySelectorAll(TARGET_INPUT_SELECTOR).forEach((input) => {
      const row = input.closest(TARGET_ROW_SELECTOR);
      const unavailable = used.has(input.dataset.targetKey);
      const selected = preserveSelection && !unavailable && row?.getAttribute("aria-checked") === "true";
      input.disabled = !selected;
      row?.setAttribute("aria-disabled", unavailable.toString());
      row?.setAttribute("aria-checked", selected.toString());
      if (row) row.tabIndex = unavailable ? -1 : 0;
      const alreadyAdded = row?.querySelector("[data-target-already-added]");
      const indicator = row?.querySelector("[data-target-available-indicator]");
      if (alreadyAdded) alreadyAdded.hidden = !unavailable;
      if (indicator) indicator.hidden = unavailable;
    });
  }

  updateSectionMenus() {
    this.editorSections.forEach((section, index, sections) => {
      const up = section.querySelector("[data-section-action='up']");
      const down = section.querySelector("[data-section-action='down']");
      this.setMenuItemDisabled(up, index === 0);
      this.setMenuItemDisabled(down, index === sections.length - 1);
    });
  }

  canAddItems(section, count) {
    if (this.itemsInSection(section).length + count <= this.maxItemsValue) return true;
    this.itemLimitReached({ target: section });
    return false;
  }

  itemLimitReached(event) {
    const section = event.target.closest("[data-editor-section]");
    section?.querySelectorAll("[data-item-limit-error]").forEach((message) => {
      message.textContent = this.itemLimitMessageValue;
      message.hidden = false;
    });
  }

  updateItemMenus() {
    this.activeSections.forEach((section) => {
      const items = this.itemsInSection(section);
      const full = items.length >= this.maxItemsValue;
      const addButton = section.querySelector("[data-add-item]");
      if (addButton) addButton.disabled = full;
      section.querySelectorAll("[data-item-limit-error]").forEach((message) => {
        message.textContent = this.itemLimitMessageValue;
        message.hidden = !full;
      });
      items.forEach((item, index) => {
        this.setMenuItemDisabled(item.querySelector("[data-item-action='up']"), index === 0);
        this.setMenuItemDisabled(item.querySelector("[data-item-action='down']"), index === items.length - 1);
      });
    });
  }

  setMenuItemDisabled(item, disabled) {
    if (!item) return;
    item.disabled = disabled;
    item.setAttribute("aria-disabled", disabled.toString());
  }

  afterSectionMove(section) {
    this.updateSectionMenus();
    this.queuePreviewRefresh();
    this.updateControls();
    requestAnimationFrame(() => section.querySelector("[data-link-in-bio-editor-options-menu-target~='button']")?.focus());
  }

  afterItemMove(item) {
    this.updateItemMenus();
    this.queuePreviewRefresh();
    this.updateControls();
    requestAnimationFrame(() => this.menuButtonFor(item.querySelector("[data-item-action]"))?.focus());
  }

  queuePreviewRefresh(mode = "all") {
    this.pendingPreviewRefreshMode = mode === "all" || this.pendingPreviewRefreshMode === "all" ? "all" : mode;
    window.cancelAnimationFrame(this.previewFrameRequest);
    this.previewFrameRequest = window.requestAnimationFrame(() => {
      const pendingMode = this.pendingPreviewRefreshMode || "all";
      this.pendingPreviewRefreshMode = null;
      this.refreshPreview(pendingMode);
    });
  }

  updateControls() {
    const current = this.currentSnapshot;
    const edited = this.observedSnapshot !== undefined && current !== this.observedSnapshot;
    this.observedSnapshot = current;
    if (edited) {
      this.saveError = false;
    }
    this.localDirty = current !== this.savedBaseline;
    this.publishDirty = !this.hasPublished || current !== this.publishedBaseline;
    this.discardDirty = current !== this.publishedBaseline;
    if (this.hasPublishButtonTarget) {
      this.publishButtonTarget.disabled = this.isSaving || !this.publishDirty;
      this.publishButtonTarget.hidden = !this.publishDirty;
    }
    if (this.hasLiveStateTarget) this.liveStateTarget.hidden = this.publishDirty;
    if (this.hasDiscardButtonTarget) {
      this.discardButtonTarget.disabled = this.isSaving;
      this.discardButtonTarget.hidden = !this.discardDirty;
    }
    if (this.hasPublicLinkTarget) {
      this.publicLinkTarget.setAttribute("aria-disabled", (!this.hasPublished).toString());
      this.publicLinkTarget.tabIndex = this.hasPublished ? 0 : -1;
      if (this.hasPublished && this.publicUrlValue) this.publicLinkTarget.href = this.publicUrlValue;
      else this.publicLinkTarget.removeAttribute("href");
    }
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.toggleAttribute("inert", !this.hasPublished);
      this.copyButtonTarget.setAttribute("aria-disabled", (!this.hasPublished).toString());
      const copy = this.copyButtonTarget.querySelector("button");
      if (copy) copy.disabled = !this.hasPublished;
    }
    const state = this.isSaving ? "saving" : this.saveError ? "error" : this.localDirty ? "pending" : "saved";
    if (this.hasSaveStateTarget) this.saveStateTarget.dataset.state = state;
    if (this.hasSaveLabelTarget) this.saveLabelTarget.textContent = this.isSaving ? this.savingMessageValue
      : this.saveError ? this.saveFailedMessageValue : this.localDirty ? this.savingMessageValue : this.savedMessageValue;
    if (edited) this.scheduleAutosave();
  }

  setSaving(saving, { lockEditor = true } = {}) {
    this.isSaving = saving;
    this.element.setAttribute("aria-busy", saving.toString());
    this.setEditorLocked(Boolean(this.pendingDestination) || (saving && lockEditor));
    this.updateControls();
  }

  setEditorLocked(locked) {
    if (locked && !this.disabledDuringSave) {
      this.disabledDuringSave = new Map(
        [...this.element.querySelectorAll("input, textarea, select, button")].map((control) => [control, control.disabled])
      );
      this.disabledDuringSave.forEach((_disabled, control) => { control.disabled = true; });
    } else if (!locked && this.disabledDuringSave) {
      this.disabledDuringSave.forEach((disabled, control) => {
        if (control.isConnected) control.disabled = disabled;
      });
      this.disabledDuringSave = null;
    }
  }

  showPublishedDialog(publicUrl) {
    if (publicUrl) {
      this.publicUrlValue = publicUrl;
      if (this.hasPublicLinkTarget) {
        this.publicLinkTarget.href = publicUrl;
      }
      const dialog = document.getElementById("link-in-bio-live-dialog");
      dialog?.setAttribute("data-link-in-bio-editor-live-dialog-url-value", publicUrl);
      dialog?.querySelectorAll("[data-live-page-url]").forEach((link) => { link.href = publicUrl; });
    }
    window.requestAnimationFrame(() => document.getElementById("link-in-bio-live-dialog")?.showModal());
  }

  resumeNavigation() {
    const destination = this.pendingDestination;
    this.pendingDestination = null;
    if (!destination) return;
    this.allowNavigation = true;
    this.beforeCache();
    if (window.Turbo) window.Turbo.visit(destination);
    else window.location.assign(destination);
  }

  announce(message, error = false) {
    window.clearTimeout(this.saveErrorTimer);
    this.clearErrorFlash();
    if (!error) return;

    const flash = document.querySelector("#flash");
    if (!flash) return;
    const messageElement = document.createElement("div");
    messageElement.className = "flash-message alert--error";
    messageElement.dataset.controller = "flash-message";
    messageElement.dataset.flashMessageTimeoutValue = "3";
    messageElement.dataset.flashMessageTarget = "message";
    messageElement.dataset.linkInBioErrorFlash = "true";
    messageElement.setAttribute("role", "alert");

    const wrap = document.createElement("div");
    wrap.className = "flash__wrap";
    const content = document.createElement("section");
    content.className = "flash__content";
    const text = document.createElement("p");
    text.textContent = message;
    content.append(text);
    wrap.append(content);
    messageElement.append(wrap);
    flash.append(messageElement);

    this.saveErrorTimer = window.setTimeout(() => {
      this.clearFieldErrors();
      this.clearErrorFlash();
      this.saveErrorTimer = null;
    }, 3000);
  }

  clearErrorFlash() {
    document.querySelector("#flash [data-link-in-bio-error-flash]")?.remove();
  }

  reportSaveError(message, intent, details) {
    this.saveError = true;
    this.markFieldErrors(details?.errors);
    console.error(`[Link in Bio] ${intent} failed`, details);
    this.announce(this.messageForErrors(details?.errors) || message, true);
    this.updateControls();
  }

  messageForErrors(errors) {
    const paths = this.errorPaths(errors);
    const labels = [...new Set(paths.map((path) => this.errorLabel(path)).filter(Boolean))];
    return labels.length ? `${this.invalidMessageValue}: ${labels.join(", ")}` : "";
  }

  errorPaths(errors) {
    if (Array.isArray(errors)) return errors.map((error) => error.field).filter(Boolean);
    return Object.keys(errors || {});
  }

  errorLabel(path) {
    if (path instanceof Element) {
      if (path.dataset.mobileInlineField === "section_name") return this.invalidFieldLabelsValue.section_name;
      if (path.dataset.editorField === "name" && path.closest("[data-editor-section]")) return this.invalidFieldLabelsValue.section_name;
      return this.invalidFieldLabelsValue[path.dataset.editorField || path.dataset.editorItemField];
    }
    if (path.startsWith("sections.") && path.endsWith(".name")) return this.invalidFieldLabelsValue.section_name;
    return this.invalidFieldLabelsValue[path] || this.invalidFieldLabelsValue[path.split(".").pop()];
  }

  markFieldErrors(errors = {}) {
    this.clearFieldErrors();
    this.errorPaths(errors).forEach((path) => {
      this.fieldsForError(path).forEach((field) => {
        field.classList.add("lib-editor__field--error");
        field.dataset.editorError = "true";
        field.dataset.editorErrorKey = this.errorKeyForField(field);
        field.setAttribute("aria-invalid", "true");
        field.closest(".lib-editor__mobile-inline-field")?.classList.add("lib-editor__mobile-inline-field--error");
      });
    });
  }

  clearFieldErrors() {
    this.element.querySelectorAll("[data-editor-error]").forEach((field) => {
      field.classList.remove("lib-editor__field--error");
      field.removeAttribute("aria-invalid");
      delete field.dataset.editorError;
      delete field.dataset.editorErrorKey;
    });
    this.element.querySelectorAll(".lib-editor__mobile-inline-field--error").forEach((field) => {
      field.classList.remove("lib-editor__mobile-inline-field--error");
    });
  }

  clearFieldError(field) {
    const key = this.errorKeyForField(field);
    if (!key) return;
    this.element.querySelectorAll(`[data-editor-error-key='${CSS.escape(key)}']`).forEach((errorField) => {
      errorField.classList.remove("lib-editor__field--error");
      errorField.removeAttribute("aria-invalid");
      delete errorField.dataset.editorError;
      delete errorField.dataset.editorErrorKey;
      errorField.closest(".lib-editor__mobile-inline-field")?.classList.remove("lib-editor__mobile-inline-field--error");
    });
  }

  fieldsForError(path) {
    if (path instanceof Element) {
      const key = this.errorKeyForField(path);
      return [...this.element.querySelectorAll("input, textarea, select")].filter((field) => this.errorKeyForField(field) === key);
    }
    const direct = [...this.element.querySelectorAll(`[data-editor-field='${CSS.escape(path)}'], [data-mobile-inline-field='${CSS.escape(path)}'], [data-editor-item-field='${CSS.escape(path)}']`)];
    if (direct.length) return direct;

    const sectionMatch = path.match(/^sections\.(\d+)\.(name|layout)$/);
    if (sectionMatch) {
      const section = this.activeSections[Number(sectionMatch[1])];
      const fields = section ? [section.querySelector(`[data-editor-field='${sectionMatch[2]}']`)] : [];
      if (sectionMatch[2] === "name" && section) {
        const preview = this.element.querySelector(`[data-link-in-bio-preview-section-id='${CSS.escape(section.dataset.clientKey)}']`);
        fields.push(preview?.querySelector("[data-mobile-inline-field='section_name']"));
      }
      return fields.filter(Boolean);
    }

    const itemMatch = path.match(/^sections\.(\d+)\.items\.(\d+)\.(external_title|external_url)$/);
    if (itemMatch) {
      const section = this.activeSections[Number(itemMatch[1])];
      const item = section && this.itemsInSection(section)[Number(itemMatch[2])];
      return item ? [item.querySelector(`[data-editor-item-field='${itemMatch[3]}']`)].filter(Boolean) : [];
    }

    return [];
  }

  errorKeyForField(field) {
    if (!field) return "";
    if (field.dataset.editorField === "display_name" || field.dataset.mobileInlineField === "display_name") return "display_name";
    if (field.dataset.editorField === "bio" || field.dataset.mobileInlineField === "bio") return "bio";
    if (field.dataset.mobileInlineField === "section_name") return `${field.closest("[data-link-in-bio-preview-section-id]")?.dataset.linkInBioPreviewSectionId}:name`;
    const section = field.closest("[data-editor-section]");
    if (section && field.dataset.editorField) return `${section.dataset.clientKey}:${field.dataset.editorField}`;
    const item = field.closest("[data-editor-item]");
    if (item && field.dataset.editorItemField) return `${item.dataset.clientKey}:${field.dataset.editorItemField}`;
    return field.dataset.editorField || field.dataset.editorItemField || "";
  }

  fieldValue(name) {
    return this.element.querySelector(`[data-editor-field='${name}']`)?.value || "";
  }

  syncEditorInitials(name) {
    const controllerRoot = this.element.querySelector("[data-controller~='link-in-bio-editor-profile-picture']");
    const initials = controllerRoot?.querySelector("[class*='profile_picture-initials']");
    if (initials) initials.textContent = this.initialsFor(name);
  }

  get socialState() {
    const root = this.element.querySelector("[data-controller~='link-in-bio-editor-social-controls']");
    return {
      links: this.jsonAttribute(root, "data-link-in-bio-editor-social-controls-links-value", {}),
      hiddenPlatforms: this.jsonAttribute(root, "data-link-in-bio-editor-social-controls-hidden-platforms-value", []),
    };
  }

  jsonAttribute(element, name, fallback) {
    try {
      return JSON.parse(element?.getAttribute(name) || JSON.stringify(fallback));
    } catch (_error) {
      return fallback;
    }
  }

  snapshotFor(payload, fileSignatures = []) {
    return JSON.stringify({ payload: this.comparablePayload(payload), files: [...fileSignatures].sort() });
  }

  comparablePayload(payload) {
    const source = payload || {};
    const comparable = {
      display_name: source.display_name || "",
      bio: source.bio || "",
      theme: source.theme || "dark",
      social_links: source.social_links || {},
      hidden_social_platforms: [...(source.hidden_social_platforms || [])].map(String).sort(),
      sections: (source.sections || []).map((section) => ({
        client_key: section.client_key,
        id: this.numericId(section.id),
        name: section.name || "",
        layout: section.layout || "list",
        items: (section.items || []).map((item) => ({
          client_key: item.client_key,
          id: this.numericId(item.id),
          kind: item.kind,
          target_sgid: item.kind === "bryght" && !item.id ? (item.target_sgid || null) : null,
          external_title: item.kind === "external" ? (item.external_title || "") : null,
          external_url: item.kind === "external" ? (item.external_url || "") : null,
          remove_thumbnail: Boolean(item.remove_thumbnail),
        })),
      })),
      deleted_section_ids: [...(source.deleted_section_ids || [])].map(String).sort(),
      deleted_item_ids: [...(source.deleted_item_ids || [])].map(String).sort(),
    };
    return this.sortObjectKeys(comparable);
  }

  sortObjectKeys(value) {
    if (Array.isArray(value)) return value.map((item) => this.sortObjectKeys(item));
    if (!value || typeof value !== "object") return value;
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = this.sortObjectKeys(value[key]);
      return result;
    }, {});
  }

  get currentSnapshot() {
    return this.snapshotFor(this.serializePayload(), this.fileSignatures());
  }

  fileSignatures() {
    const signatures = [];
    const profile = this.element.querySelector("[data-link-in-bio-editor-profile-file]")?.files?.[0];
    if (profile) signatures.push(`profile:${this.fileSignature(profile)}`);
    else if (this.draftAssetKeysValue.includes("profile_picture")) signatures.push("profile:persisted-draft");
    this.activeSections.forEach((section) => {
      section.querySelectorAll(":scope > [data-item-list] > [data-editor-item]").forEach((item) => {
        const removal = this.serializeItem(item).remove_thumbnail;
        const file = this.itemFile(item);
        const assetKey = `thumbnail:${item.dataset.clientKey}`;
        if (file && !removal) signatures.push(`${assetKey}:${this.fileSignature(file)}`);
        else if (!removal && this.draftAssetKeysValue.includes(assetKey)) signatures.push(`${assetKey}:persisted-draft`);
      });
    });
    return signatures.sort();
  }

  persistedAssetSignatures() {
    const removedThumbnails = new Set((this.editorPayloadValue.sections || []).flatMap((section) =>
      (section.items || []).filter((item) => item.remove_thumbnail).map((item) => `thumbnail:${item.client_key}`)));
    return this.draftAssetKeysValue.filter((key) => {
      if (key === "profile_picture") return true;
      return !removedThumbnails.has(key);
    }).map((key) => key === "profile_picture" ? "profile:persisted-draft" : `${key}:persisted-draft`).sort();
  }

  captureDraftAssetKeys(payload, files) {
    const keys = [];
    if (files.some((signature) => signature.startsWith("profile:"))) keys.push("profile_picture");
    (payload.sections || []).forEach((section) => {
      (section.items || []).forEach((item) => {
        const key = `thumbnail:${item.client_key}`;
        if (!item.remove_thumbnail && files.some((signature) => signature.startsWith(`${key}:`))) keys.push(key);
      });
    });
    this.draftAssetKeysValue = keys;
  }

  clearCommittedFileInputs() {
    const profile = this.element.querySelector("[data-link-in-bio-editor-profile-file]");
    if (profile) profile.value = "";
    this.element.querySelectorAll(THUMBNAIL_INPUT_SELECTOR).forEach((input) => {
      const item = input.closest("[data-editor-item]");
      const accepted = item && this.itemEditSnapshots.get(item);
      if (accepted) {
        accepted.thumbnail.files = [];
        accepted.thumbnail.removal = "0";
        accepted.pendingFile = undefined;
        accepted.payload.remove_thumbnail = false;
      } else input.value = "";
    });
    this.pendingThumbnailFiles.clear();
  }

  fileSignature(file) {
    return `${file.name}:${file.size}:${file.lastModified}:${file.type}`;
  }

  cloneTemplate(template, placeholder, clientKey) {
    const shell = document.createElement("template");
    shell.innerHTML = template.innerHTML.replaceAll(placeholder, clientKey).trim();
    const clone = shell.content.firstElementChild;
    clone?.querySelectorAll("[data-controller~='link-in-bio-editor-options-menu']").forEach((menu, index) => {
      const button = menu.querySelector("[data-link-in-bio-editor-options-menu-target~='button']");
      const container = menu.querySelector("[role='menu']");
      if (!button) return;
      button.id = `options-menu-${clientKey}-${index}`;
      container?.setAttribute("aria-labelledby", button.id);
    });
    return clone;
  }

  newClientKey(prefix) {
    const id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}-${id}`;
  }

  numericId(value) {
    if (value === undefined || value === null || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }

  itemsInSection(section) {
    if (!section) return [];
    return [...section.querySelectorAll(":scope > [data-item-list] > [data-editor-item]")];
  }

  menuButtonFor(element) {
    const menu = element?.closest(".option__anchor") || element?.querySelector?.(".option__anchor");
    return menu?.querySelector("[data-link-in-bio-editor-options-menu-target~='button']");
  }

  initialsFor(name) {
    return name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";
  }

  setHeroPictureOrientation(picture) {
    const image = picture?.querySelector("img");
    if (!picture || !image) return;
    const update = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      const orientation = image.naturalWidth > image.naturalHeight
        ? "landscape"
        : (image.naturalWidth === image.naturalHeight ? "square" : "portrait");
      picture.dataset.imageOrientation = orientation;
      image.closest("picture")?.classList.add(`c-responsive_image-${orientation}`);
    };
    if (image.complete) update();
    else image.addEventListener("load", update, { once: true });
  }

  async readJson(response) {
    try {
      return await response.json();
    } catch (error) {
      return { ok: false, errors: { response: error.message, contentType: response.headers.get("content-type") } };
    }
  }

  get editorSections() {
    return [...this.element.querySelectorAll("[data-editor-section]")];
  }

  get activeSections() {
    return [...this.element.querySelectorAll("[data-editor-section]:not([data-provisional])")];
  }

  get sectionsList() {
    return this.element.querySelector("#link-in-bio-editor-sections");
  }

  get previewRoot() {
    return this.hasPreviewContentTarget ? this.previewContentTarget.querySelector(`.${PAGE_CLASS}-root`) : null;
  }
}
